import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ChevronLeft, 
  Plus,
  Trash2,
  Edit2,
  FlaskConical,
  Calendar,
  Search,
  Filter,
  Download,
  BookOpen,
  X,
  Check,
  ChevronDown,
  TrendingUp,
  LineChart,
  FileDown,
  GitCompare
} from 'lucide-react';
import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { exportLabResultsToPDF } from '../utils/pdfExport';
import { AttachmentSection } from '../components/AttachmentSection';
import TimelineButton from '../components/TimelineButton';
import PhotoCompareDialog from '../components/PhotoCompareDialog';
import { todayLocal, formatLocalDate } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LabTracker = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [labResults, setLabResults] = useState([]);
  const [photosOpenFor, setPhotosOpenFor] = useState({});
  const [compareOpen, setCompareOpen] = useState(false);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingResult, setEditingResult] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showTestDropdown, setShowTestDropdown] = useState(false);
  
  // Timeline chart state
  const [showTimeline, setShowTimeline] = useState(false);
  const [selectedTimelineTest, setSelectedTimelineTest] = useState('');
  const [showTimelineTestDropdown, setShowTimelineTestDropdown] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    category: '',
    test_name: '',
    value: '',
    unit: '',
    reference_range: '',
    date: todayLocal(),
    notes: '',
    lab_name: ''
  });

  useEffect(() => {
    fetchCategories();
    fetchLabResults();
  }, [token]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/labs/categories`);
      const data = await response.json();
      setCategories(data.categories || {});
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchLabResults = async () => {
    try {
      const response = await fetch(`${API_URL}/api/labs/results`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setLabResults(data.results || []);
    } catch (error) {
      console.error('Error fetching lab results:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingResult 
        ? `${API_URL}/api/labs/results/${editingResult.id}`
        : `${API_URL}/api/labs/results`;
      
      const method = editingResult ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowAddModal(false);
        setEditingResult(null);
        resetForm();
        fetchLabResults();
      }
    } catch (error) {
      console.error('Error saving lab result:', error);
    }
  };

  const handleDelete = async (resultId) => {
    if (!window.confirm('Are you sure you want to delete this lab result?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/labs/results/${resultId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchLabResults();
      }
    } catch (error) {
      console.error('Error deleting lab result:', error);
    }
  };

  const handleEdit = (result) => {
    setEditingResult(result);
    setFormData({
      category: result.category || '',
      test_name: result.test_name || '',
      value: result.value || '',
      unit: result.unit || '',
      reference_range: result.reference_range || '',
      date: result.date || todayLocal(),
      notes: result.notes || '',
      lab_name: result.lab_name || ''
    });
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({
      category: '',
      test_name: '',
      value: '',
      unit: '',
      reference_range: '',
      date: todayLocal(),
      notes: '',
      lab_name: ''
    });
  };

  const getCategoryColor = (categoryKey) => {
    const colors = {
      basic_panels: 'bg-blue-100 text-blue-800',
      inflammation: 'bg-orange-100 text-orange-800',
      infection: 'bg-red-100 text-red-800',
      autoimmunity: 'bg-purple-100 text-purple-800',
      immune_allergic: 'bg-pink-100 text-pink-800',
      tick_bourne: 'bg-green-100 text-green-800',
      stool_testing: 'bg-amber-100 text-amber-800',
      nasal_testing: 'bg-teal-100 text-teal-800',
      cunningham_panel: 'bg-indigo-100 text-indigo-800',
      micronutrients: 'bg-lime-100 text-lime-800',
      genetics: 'bg-cyan-100 text-cyan-800'
    };
    return colors[categoryKey] || 'bg-stone-100 text-stone-800';
  };

  const filteredResults = labResults.filter(result => {
    const matchesCategory = selectedCategory === 'all' || result.category === selectedCategory;
    const matchesSearch = searchTerm === '' || 
      result.test_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      result.category?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const groupedResults = filteredResults.reduce((acc, result) => {
    const date = result.date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(result);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedResults).sort((a, b) => new Date(b) - new Date(a));

  const getTestsForCategory = () => {
    if (!formData.category || !categories[formData.category]) return [];
    return categories[formData.category].tests || [];
  };

  // Get unique test names for timeline dropdown
  const uniqueTestNames = [...new Set(labResults.map(r => r.test_name))].sort();

  // Prepare chart data for selected test
  const getTimelineData = () => {
    if (!selectedTimelineTest) return [];
    
    const testResults = labResults
      .filter(r => r.test_name === selectedTimelineTest)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    
    return testResults.map(r => ({
      date: formatLocalDate(r.date, 'en-US', { month: 'short', day: 'numeric', year: '2-digit' }),
      value: parseFloat(r.value) || 0,
      fullDate: r.date,
      unit: r.unit || '',
      reference: r.reference_range || '',
      notes: r.notes || ''
    }));
  };

  // Parse reference range to get min/max values
  const parseReferenceRange = () => {
    const testResults = labResults.filter(r => r.test_name === selectedTimelineTest);
    if (testResults.length === 0) return null;
    
    const refRange = testResults[0]?.reference_range;
    if (!refRange) return null;
    
    // Try to parse formats like "10-50", "10 - 50", "<50", ">10"
    const rangeMatch = refRange.match(/(\d+\.?\d*)\s*[-–]\s*(\d+\.?\d*)/);
    if (rangeMatch) {
      return { min: parseFloat(rangeMatch[1]), max: parseFloat(rangeMatch[2]) };
    }
    return null;
  };

  const timelineData = getTimelineData();
  const referenceRange = parseReferenceRange();
  const selectedTestUnit = labResults.find(r => r.test_name === selectedTimelineTest)?.unit || '';

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-stone-200">
          <p className="font-semibold text-[#2D2D2D]">{label}</p>
          <p className="text-accent font-bold">
            {data.value} {data.unit}
          </p>
          {data.reference && (
            <p className="text-xs text-[#57534E]">Ref: {data.reference}</p>
          )}
          {data.notes && (
            <p className="text-xs text-[#57534E] mt-1 max-w-48">{data.notes}</p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-orange-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="p-2"
                data-testid="back-button"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-nunito font-bold text-[#2D2D2D]">Lab Results Tracker</h1>
                <p className="text-sm text-[#57534E]">Keep all your lab results organized</p>
              </div>
            </div>
            <Button
              onClick={() => {
                resetForm();
                setEditingResult(null);
                setShowAddModal(true);
              }}
              className="bg-accent hover:bg-accent/90"
              data-testid="add-result-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Result
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Quick Actions */}
        <div className="flex gap-3 flex-wrap">
          <TimelineButton />
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/lab-education')}
            className="flex-1 min-w-[140px]"
            data-testid="learn-about-labs-button"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Learn About Labs
          </Button>
          <Button
            variant={showTimeline ? "default" : "outline"}
            size="sm"
            onClick={() => setShowTimeline(!showTimeline)}
            className={`flex-1 min-w-[140px] ${showTimeline ? 'bg-accent hover:bg-accent/90' : ''}`}
            data-testid="timeline-toggle-button"
            disabled={uniqueTestNames.length === 0}
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            {showTimeline ? 'Hide Timeline' : 'View Timeline'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportLabResultsToPDF(labResults, categories)}
            className="flex-1 min-w-[140px]"
            data-testid="export-pdf-button"
            disabled={labResults.length === 0}
          >
            <FileDown className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCompareOpen(true)}
            className="flex-1 min-w-[140px]"
            data-testid="compare-lab-photos-btn"
          >
            <GitCompare className="w-4 h-4 mr-2" />
            Compare Photos
          </Button>
        </div>

        {/* Timeline Chart Section */}
        {showTimeline && (
          <Card className="bg-white rounded-xl border border-stone-100 p-5" data-testid="timeline-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                <LineChart className="w-5 h-5 text-accent" />
              </div>
              <div>
                <h3 className="font-nunito font-bold text-[#2D2D2D]">Lab Value Timeline</h3>
                <p className="text-sm text-[#57534E]">Track how your values change over time</p>
              </div>
            </div>

            {/* Test Selector */}
            <div className="mb-4 relative">
              <label className="block text-sm font-medium text-[#2D2D2D] mb-2">
                Select a test to visualize:
              </label>
              <Button
                variant="outline"
                onClick={() => setShowTimelineTestDropdown(!showTimelineTestDropdown)}
                className="w-full md:w-72 justify-between"
                data-testid="timeline-test-selector"
              >
                {selectedTimelineTest || 'Choose a test...'}
                <ChevronDown className="w-4 h-4" />
              </Button>
              
              {showTimelineTestDropdown && (
                <>
                  <div 
                    className="fixed inset-0 z-30" 
                    onClick={() => setShowTimelineTestDropdown(false)}
                  />
                  <div className="absolute top-full mt-1 w-full md:w-72 bg-white border border-stone-200 rounded-lg shadow-lg z-40 max-h-64 overflow-y-auto">
                    {uniqueTestNames.map((testName) => {
                      const testCount = labResults.filter(r => r.test_name === testName).length;
                      return (
                        <button
                          key={testName}
                          onClick={() => {
                            setSelectedTimelineTest(testName);
                            setShowTimelineTestDropdown(false);
                          }}
                          className={`w-full px-4 py-2 text-left hover:bg-stone-50 text-sm flex justify-between items-center ${
                            selectedTimelineTest === testName ? 'bg-accent/10 text-accent' : ''
                          }`}
                        >
                          <span>{testName}</span>
                          <span className="text-xs text-[#A8A29E]">{testCount} result{testCount !== 1 ? 's' : ''}</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Chart */}
            {selectedTimelineTest ? (
              timelineData.length >= 2 ? (
                <div className="h-64 mt-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsLineChart
                      data={timelineData}
                      margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12, fill: '#57534E' }}
                        axisLine={{ stroke: '#D6D3D1' }}
                      />
                      <YAxis 
                        tick={{ fontSize: 12, fill: '#57534E' }}
                        axisLine={{ stroke: '#D6D3D1' }}
                        label={{ 
                          value: selectedTestUnit, 
                          angle: -90, 
                          position: 'insideLeft',
                          style: { textAnchor: 'middle', fill: '#57534E', fontSize: 12 }
                        }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      {referenceRange && (
                        <>
                          <ReferenceLine 
                            y={referenceRange.min} 
                            stroke="#22C55E" 
                            strokeDasharray="5 5"
                            label={{ value: 'Low', fill: '#22C55E', fontSize: 10 }}
                          />
                          <ReferenceLine 
                            y={referenceRange.max} 
                            stroke="#22C55E" 
                            strokeDasharray="5 5"
                            label={{ value: 'High', fill: '#22C55E', fontSize: 10 }}
                          />
                        </>
                      )}
                      <Line 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#7C9885" 
                        strokeWidth={2}
                        dot={{ fill: '#7C9885', strokeWidth: 2, r: 5 }}
                        activeDot={{ r: 7, fill: '#5B7B65' }}
                      />
                    </RechartsLineChart>
                  </ResponsiveContainer>
                  {referenceRange && (
                    <div className="flex items-center justify-center gap-4 mt-2 text-xs text-[#57534E]">
                      <span className="flex items-center gap-1">
                        <span className="w-4 h-0.5 bg-green-500 inline-block" style={{ borderTop: '2px dashed #22C55E' }}></span>
                        Reference range: {referenceRange.min} - {referenceRange.max} {selectedTestUnit}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-stone-50 rounded-lg p-6 text-center">
                  <TrendingUp className="w-10 h-10 text-stone-300 mx-auto mb-2" />
                  <p className="text-[#57534E] text-sm">
                    {timelineData.length === 1 
                      ? "You need at least 2 results for this test to see a trend line."
                      : "No data available for this test."}
                  </p>
                  <p className="text-xs text-[#A8A29E] mt-1">
                    Add more results to track changes over time.
                  </p>
                </div>
              )
            ) : (
              <div className="bg-stone-50 rounded-lg p-6 text-center">
                <LineChart className="w-10 h-10 text-stone-300 mx-auto mb-2" />
                <p className="text-[#57534E] text-sm">
                  Select a test above to see how values change over time.
                </p>
              </div>
            )}
          </Card>
        )}

        {/* Filters */}
        <Card className="bg-white rounded-xl border border-stone-100 p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-[#57534E]" />
              <Input
                placeholder="Search tests..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            {/* Category Filter */}
            <div className="relative">
              <Button
                variant="outline"
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className="w-full md:w-48 justify-between"
                data-testid="category-filter-button"
              >
                <span className="flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  {selectedCategory === 'all' ? 'All Categories' : categories[selectedCategory]?.name || selectedCategory}
                </span>
                <ChevronDown className="w-4 h-4" />
              </Button>
              
              {showCategoryDropdown && (
                <div className="absolute top-full mt-1 w-full bg-white border border-stone-200 rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
                  <button
                    onClick={() => {
                      setSelectedCategory('all');
                      setShowCategoryDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-stone-50 text-sm"
                  >
                    All Categories
                  </button>
                  {Object.entries(categories).map(([key, cat]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setSelectedCategory(key);
                        setShowCategoryDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-stone-50 text-sm"
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white rounded-xl border border-stone-100 p-4 text-center">
            <p className="text-2xl font-bold text-accent">{labResults.length}</p>
            <p className="text-xs text-[#57534E]">Total Results</p>
          </Card>
          <Card className="bg-white rounded-xl border border-stone-100 p-4 text-center">
            <p className="text-2xl font-bold text-accent">
              {new Set(labResults.map(r => r.category)).size}
            </p>
            <p className="text-xs text-[#57534E]">Categories</p>
          </Card>
          <Card className="bg-white rounded-xl border border-stone-100 p-4 text-center">
            <p className="text-2xl font-bold text-accent">
              {new Set(labResults.map(r => r.date)).size}
            </p>
            <p className="text-xs text-[#57534E]">Test Dates</p>
          </Card>
          <Card className="bg-white rounded-xl border border-stone-100 p-4 text-center">
            <p className="text-2xl font-bold text-accent">
              {new Set(labResults.map(r => r.test_name)).size}
            </p>
            <p className="text-xs text-[#57534E]">Unique Tests</p>
          </Card>
        </div>

        {/* Results List */}
        {loading ? (
          <Card className="bg-white rounded-xl border border-stone-100 p-8 text-center">
            <p className="text-[#57534E]">Loading lab results...</p>
          </Card>
        ) : sortedDates.length === 0 ? (
          <Card className="bg-white rounded-xl border border-stone-100 p-8 text-center">
            <FlaskConical className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="font-semibold text-[#2D2D2D] mb-2">No Lab Results Yet</h3>
            <p className="text-sm text-[#57534E] mb-4">Start tracking your child's lab results to keep everything organized.</p>
            <Button
              onClick={() => {
                resetForm();
                setEditingResult(null);
                setShowAddModal(true);
              }}
              className="bg-accent hover:bg-accent/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Result
            </Button>
          </Card>
        ) : (
          <div className="space-y-6">
            {sortedDates.map(date => (
              <div key={date}>
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-[#57534E]" />
                  <h3 className="font-semibold text-[#2D2D2D]">
                    {formatLocalDate(date, 'en-US', { 
                      weekday: 'long',
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </h3>
                  <span className="text-sm text-[#57534E]">({groupedResults[date].length} results)</span>
                </div>
                <div className="space-y-2">
                  {groupedResults[date].map((result, index) => (
                    <Card 
                      key={result.id || index} 
                      className="bg-white rounded-xl border border-stone-100 p-4"
                      data-testid={`lab-result-${result.id || index}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryColor(result.category)}`}>
                              {categories[result.category]?.name || result.category}
                            </span>
                          </div>
                          <h4 className="font-semibold text-[#2D2D2D]">{result.test_name}</h4>
                          <div className="flex items-baseline gap-2 mt-1">
                            <span className="text-xl font-bold text-accent">{result.value}</span>
                            {result.unit && <span className="text-sm text-[#57534E]">{result.unit}</span>}
                          </div>
                          {result.reference_range && (
                            <p className="text-xs text-[#57534E] mt-1">
                              Reference: {result.reference_range}
                            </p>
                          )}
                          {result.lab_name && (
                            <p className="text-xs text-[#57534E]">Lab: {result.lab_name}</p>
                          )}
                          {result.notes && (
                            <p className="text-sm text-[#57534E] mt-2 bg-stone-50 p-2 rounded">
                              {result.notes}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(result)}
                            className="p-2 hover:bg-stone-100"
                            data-testid={`edit-${result.id || index}`}
                          >
                            <Edit2 className="w-4 h-4 text-[#57534E]" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(result.id)}
                            className="p-2 hover:bg-red-50"
                            data-testid={`delete-${result.id || index}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                      {result.id && (
                        <div className="mt-3 pt-3 border-t border-stone-100">
                          {photosOpenFor[result.id] ? (
                            <AttachmentSection
                              parentType="lab"
                              parentId={result.id}
                              token={token}
                              title="Lab Report Photos"
                            />
                          ) : (
                            <button
                              type="button"
                              onClick={() => setPhotosOpenFor(prev => ({ ...prev, [result.id]: true }))}
                              className="text-xs text-primary hover:underline flex items-center gap-1"
                              data-testid={`open-photos-${result.id}`}
                            >
                              <Plus className="w-3 h-3" /> Attach lab report photo
                            </button>
                          )}
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-nunito font-bold text-[#2D2D2D]">
                  {editingResult ? 'Edit Lab Result' : 'Add Lab Result'}
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingResult(null);
                    resetForm();
                  }}
                  className="p-2"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                    Date *
                  </label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    data-testid="date-input"
                  />
                </div>

                {/* Category */}
                <div className="relative">
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                    Category *
                  </label>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                    className="w-full justify-between"
                    data-testid="category-select"
                  >
                    {formData.category ? categories[formData.category]?.name || formData.category : 'Select category...'}
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                  
                  {showCategoryDropdown && (
                    <div className="absolute top-full mt-1 w-full bg-white border border-stone-200 rounded-lg shadow-lg z-30 max-h-48 overflow-y-auto">
                      {Object.entries(categories).map(([key, cat]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, category: key, test_name: '' });
                            setShowCategoryDropdown(false);
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-stone-50 text-sm"
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Test Name */}
                <div className="relative">
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                    Test Name *
                  </label>
                  {formData.category ? (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowTestDropdown(!showTestDropdown)}
                        className="w-full justify-between"
                        data-testid="test-select"
                      >
                        {formData.test_name || 'Select test...'}
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                      
                      {showTestDropdown && (
                        <div className="absolute top-full mt-1 w-full bg-white border border-stone-200 rounded-lg shadow-lg z-30 max-h-48 overflow-y-auto">
                          {getTestsForCategory().map((test, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => {
                                setFormData({ ...formData, test_name: test });
                                setShowTestDropdown(false);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-stone-50 text-sm"
                            >
                              {test}
                            </button>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, test_name: '' });
                              setShowTestDropdown(false);
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-stone-50 text-sm text-accent"
                          >
                            + Enter custom test name
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <Input
                      value={formData.test_name}
                      onChange={(e) => setFormData({ ...formData, test_name: e.target.value })}
                      placeholder="Enter test name"
                      required
                      data-testid="test-name-input"
                    />
                  )}
                  
                  {formData.category && !getTestsForCategory().includes(formData.test_name) && formData.test_name === '' && (
                    <Input
                      className="mt-2"
                      value={formData.test_name}
                      onChange={(e) => setFormData({ ...formData, test_name: e.target.value })}
                      placeholder="Or type custom test name"
                      data-testid="custom-test-input"
                    />
                  )}
                </div>

                {/* Value and Unit */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                      Value *
                    </label>
                    <Input
                      value={formData.value}
                      onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                      placeholder="e.g., 45"
                      required
                      data-testid="value-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                      Unit
                    </label>
                    <Input
                      value={formData.unit}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      placeholder="e.g., mg/dL"
                      data-testid="unit-input"
                    />
                  </div>
                </div>

                {/* Reference Range */}
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                    Reference Range
                  </label>
                  <Input
                    value={formData.reference_range}
                    onChange={(e) => setFormData({ ...formData, reference_range: e.target.value })}
                    placeholder="e.g., 10-50 mg/dL"
                    data-testid="reference-range-input"
                  />
                </div>

                {/* Lab Name */}
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                    Lab Name
                  </label>
                  <Input
                    value={formData.lab_name}
                    onChange={(e) => setFormData({ ...formData, lab_name: e.target.value })}
                    placeholder="e.g., Quest Diagnostics"
                    data-testid="lab-name-input"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Any additional notes about this result..."
                    className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm min-h-[80px] resize-none focus:outline-none focus:ring-2 focus:ring-accent/50"
                    data-testid="notes-input"
                  />
                </div>

                {/* Submit Button */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingResult(null);
                      resetForm();
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-accent hover:bg-accent/90"
                    data-testid="save-result-button"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {editingResult ? 'Update Result' : 'Save Result'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      <PhotoCompareDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        token={token}
        parentType="lab"
        title="Compare Lab Report Photos"
        description="Compare two lab printouts side-by-side — perfect for tracking titers (e.g., ASO, Anti-DNase B) over time. Sorted oldest → newest."
        filterOptions={Array.from(new Set(labResults.map(r => r.test_name).filter(Boolean)))}
        filterLabel="All tests"
      />
    </div>
  );
};

export default LabTracker;
